import { action, observable, makeObservable } from 'mobx';
import {Uri} from './uri/uri';
import {ILocationService} from './location.service.interface';
import {decodeUriComponentRecursive, isAbsolutUrl, sanitizeUrl} from '../../../utils/url-utils';
import {IStorageService} from '../storage/storage.service.interface';
import {UriPath} from './uri-path/uri-path';

export const ORBIT_LOCATION_OVERRIDE_BASE = 'orbit_location_override_base';


export class LocationService implements ILocationService {

  constructor(private _window: Partial<Window>, private _localStorage: IStorageService, private _sendTechnicalEvent?: (action: string, label?: string, value?: any) => void) {
    makeObservable(this);

    this._initCurrentUri();

    this._checkUrlIsNullString(this._window.location.pathname, 'constructor');

    this._window.addEventListener('popstate', () => {
      this._checkUrlIsNullString(this._window.location.pathname, 'popstate');
      this._updateCurrentUriAsync();
    });
  }

  private _previousUri: Uri = null;
  @observable.ref
  currentUri: Uri = null;

  @action.bound
  private _setCurrentUri(newUri: Uri) {
    this._previousUri = this.currentUri;
    this.currentUri = newUri;
  }


  private _initCurrentUri() {
    this._setCurrentUri(this._createNewUriForCurrentLocation());
    if(this._window.location.href !== this.currentUri.toString()) {
        this._window.history.replaceState(null, '', this.currentUri.toString());
    }
  }

  enableBasePathOverride(): void {
    const basePathName = this.currentUri.pathName.getBase().toString().toLowerCase();
    this._localStorage.setItem(ORBIT_LOCATION_OVERRIDE_BASE, basePathName.toString());
  }

  private _createNewUriForCurrentLocation(): Uri {
    let locationOverride = sanitizeUrl(this._window.location.href);
    return this.tryParseUrl(this._applyLocationOverride(locationOverride));
  }

  private _fireOnBeforeChangingLocation(targetUri: Uri) {
    this._onBeforeChangingLocationSubscribers.forEach(handler => {
      try {
        handler(targetUri);
      } catch (err) {
        console.error('OnBeforeChangingLocation handler failed!', err);
      }
    });
  }
  private _updateCurrentUriAsync(): Promise<void> {
    let newUri = this._createNewUriForCurrentLocation();

    this._fireOnBeforeChangingLocation(newUri);

    // we set the current location async so we can give the spinner the chance to show up.
    return new Promise<void>((resolve, reject) => {
      // Never remove this setTimeout- without this, while you click menu items to move between arenas, the spinner animation will not be smooth.
      setTimeout(() => {
        try {
          this._setCurrentUri(newUri);
          resolve();
        } catch (err) {
          reject(err);
        }
      }, 0);
    });

  }

  private _onBeforeChangingLocationSubscribers: Array<(targetUri: Uri) => void> = [];
  onBeforeChangingLocation(handler: (targetUri: Uri) => void): void {
    this._onBeforeChangingLocationSubscribers.push(handler);
  }


  private _isSameAsCurrentUrl(newUrl: string): boolean {

    let result:boolean;
    if(isAbsolutUrl(newUrl)) {
      result = Boolean(this._window.location.href === newUrl);
    } else {
      result = Boolean(newUrl === this._window.location.pathname + this._window.location.search + this._window.location.hash);
    }
    return result;
  }

  private _lastUpdateCurrentUriPromise: Promise<void> = Promise.resolve();
  private _executeNavigation(url: string, historyApiCallback: (newUrl: string) => void) : Promise<void> {
    url = (url || '').trim();
    url = url || '/';

    this._checkUrlIsNullString(url, 'push');

    if(this._isRedirectUrl(url)) {
      return this.redirect(url);
    } else {

      const newUrl = this._applyLocationOverride(url);

      if(!this._isSameAsCurrentUrl(newUrl)) {
        historyApiCallback(newUrl);
      }

      if(!this._isSameAsCurrentUrl(this.currentUri.toString())) {
        this._lastUpdateCurrentUriPromise = this._lastUpdateCurrentUriPromise.then(() => this._updateCurrentUriAsync());
      }

      // This is for making very quick subsequent calls to this function to happen in the order they were called.
      // related bug: BUG 1668336
      return this._lastUpdateCurrentUriPromise;

    }
  }

  /**
   * @param {string} url
   * url parameter can be a full url or just a relative url:
   *    url parameter examples:
   *      https://www.888casino.com/slots
   *      /slots
   *      /slots#filters=someFilter
   *      #justTheHash
   * @param {boolean} forceHistoryPush
   * used by the "browser-buttons-handler" to overwrite the current url;
   */
  push(url: string): Promise<void> {
    return this._executeNavigation(url, (newUrl) => this._window.history.pushState(null, '', newUrl));
  }

  pushWithoutValidation(url){
    this._window.history.pushState(null, '', url);
  }

  goBack(index = -1){
    this._window.history.go(index);
  }

  /**
   * @param {string} url
   * url parameter can be a full url or just a relative url:
   *    url parameter examples:
   *      https://www.888casino.com/slots
   *      /slots
   *      /slots#filters=someFilter
   *      #justTheHash
   */
  replace(url: string): Promise<void> {
    return this._executeNavigation(url, (newUrl) => this._window.history.replaceState(null, '', newUrl));
  }


  redirect(url: string): Promise<void> {
    this._window.location.href = url;
    return Promise.resolve();
  }

  goHome(): Promise<void> {
    return this.push('/');
  }



  private _isRedirectUrl(url: string): boolean {
    if(url && url[0] === '#') {
      return false;
    }

    let parsedUrl = this.tryParseUrl(url);
    if(parsedUrl && parsedUrl.origin.toLowerCase() !== this.currentUri.origin.toLowerCase()) {
      return true;
    } else {
      return false;
    }
  }

  get hasNavigationHistory(): boolean {
    if(this._previousUri) {
      return true;
    }
    return false;
  }

  tryParseUrl(url: string): Uri {
    try {
      return new Uri(new URL(this._tryDecodeUrl(url)));
    } catch (e) {
      return null;
    }
  }

  private _tryDecodeUrl(url: string): string {
    try {
      return decodeUriComponentRecursive(url);
    } catch(err) {
      console.error('Failed to decode url: ' + url);
      return url;
    }
  }

  buildFullUri(partialUri: string): Uri {
    const candidate = partialUri?.toString() || '/';
    return new Uri(new URL(candidate, this.currentUri.toString()));
  }

  private _checkUrlIsNullString(url: string, functionName: string): void {

    try {
      if(url === '/null' || url === 'null' || url === '/null/' || url === 'null/') {
        let message = new Error(`null route detected! Action=${functionName}; CurrentLocation=${this._window.location.href}; DocumentReferrer: ${document.referrer}`).stack;
        if(this._sendTechnicalEvent) {
          this._sendTechnicalEvent('NULL_ROUTE_DETECTED', functionName, message);
        }

        console.error(message);

      }
    } catch (err) {
      console.error('_checkUrlIsNullString Failed', err);
    }

  }

  private _applyLocationOverride(url: string): string {
      return this._getLocationOverrideStrategy().overrideLocation(url);
  }


  private _getLocationOverrideStrategy(): ILocationOverrideStrategy {
    const basePath = this._localStorage.getItem(ORBIT_LOCATION_OVERRIDE_BASE);
    if(basePath) {
      return new LocationOverrideStrategy(basePath);
    } else {
        return new NoLocationOverrideStrategy();
    }
  }

  get isHomePage(): boolean {
    return this._getLocationOverrideStrategy().isHomePage(this.currentUri.pathName);
  }
}

interface ILocationOverrideStrategy {
    overrideLocation(url: string): string;
    isHomePage(pathName: UriPath): boolean;
}


class LocationOverrideStrategy implements ILocationOverrideStrategy {

    constructor(basePath: string) {
      this._basePath = new UriPath(basePath.toLowerCase());
    }

    private readonly _basePath: UriPath;

    isHomePage(pathName: UriPath): boolean {
      return this._basePath.equals(pathName);
    }

    overrideLocation(url: string): string {
        if (url.startsWith('#')) {
            return url;
        }

        let newUrl = this._tryParseUrl(url);
        if(newUrl) {
            if (!newUrl.pathname.toLowerCase().startsWith(this._basePath.toString())) {
                newUrl.pathname = this._basePath.append(newUrl.pathname).toString();
            }

            return sanitizeUrl(newUrl.href.toString());
        } else {
          if(!url.toLowerCase().startsWith(this._basePath.toString())) {
            return this._basePath.append(url).toString();
          }
        }

        return url;
    }

    private _tryParseUrl(url): URL {
      try {
        return new URL(url);
      } catch (e) {
          return null;
      }
    }

}


class NoLocationOverrideStrategy implements ILocationOverrideStrategy {
    isHomePage(pathName: UriPath): boolean {
        return pathName.isEmpty;
    }

    overrideLocation(url: string): string {
      return url;
    }
}